<!--
  Concrete Field Testing log.

  A title, then the thirteen-column cylinder-set log (Date Cast, Truck No.,
  No. Cast, Pile Number, Location, Mix, Slump, Temperature, De Moulded,
  Specimen, 7 Day, 14 Day, 28 Day), then notes. Follows DailyReportForm.svelte
  and MixDesignForm.svelte's shape: raw string state throughout (every field
  here is already a plain string on the wire — see the design note on
  FieldTestRow in lib/types.ts), an explicit Save, add/remove/reorder rows,
  delete behind an in-page confirmation banner rather than confirm().

  Slump, temperature and the three break results are deliberately plain text
  inputs, no `inputmode="decimal"` hint and no numeric coercion anywhere in
  this file: the paper carries values like '150*' and 'Not recorded', which
  a numeric field would refuse or a decimal-only keyboard would make awkward
  to type.

  A new log starts with one blank row rather than none, so the user is not
  staring at an empty table (see EquipmentForm/DailyReportForm's own defaults
  for the same reasoning applied to their own tables).

  ROWS DISPLAY NEWEST FIRST. The stored order is untouched — rows keep their
  seq, oldest first, and the printed sheet still reads top-down the way the
  paper log does — but on screen the list is walked in reverse, so the row
  being worked on today sits at the top and "Add row" sits directly above it
  instead of below months of history. Everything indexed below is an ARRAY
  index; only the walk order is reversed, so the arrows are translated at the
  edge (visual up = array index + 1) rather than the data model changing.

  Thirteen columns cannot fit on one line anywhere inside this app's 64rem
  office container — see the layout comment in the <style> block below for
  the arithmetic — so unlike the other repeating-row tables in this feature,
  there is no one-line-per-row mode here at all. Each row is always a
  labelled block whose fields wrap via auto-fit, exactly the mechanism
  MixDesignForm's materials grid uses below its own 60rem breakpoint, just
  never switched off. That is what keeps this table from ever needing to
  scroll sideways, at any width.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { createAutosave } from '../../lib/autosave';
  import { printFieldTest } from '../../lib/export/quality-pdf';
  import SitePicker from './SitePicker.svelte';
  import { claimAddress, linkHandler, location as appLocation, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { advanceOnEnter } from '../../lib/fields/advance-on-enter';
  import { isAdmin, notify, notifyError } from '../../lib/store';
  import type { FieldTestRowInput, SaveFieldTestLogRequest, SiteSummary } from '../../lib/types';

  interface Props {
    /** null for a new log. */
    fieldTestId?: number | null;
  }

  let { fieldTestId = null }: Props = $props();

  /** One log row as held by the form. Printed column order. */
  interface RowState {
    date_cast: string;
    truck_no: string;
    no_cast: string;
    pile_number: string;
    location: string;
    mix: string;
    slump_mm: string;
    temperature_c: string;
    de_moulded: string;
    specimen: string;
    break_7_day: string;
    break_14_day: string;
    break_28_day: string;
  }

  function blankRow(): RowState {
    return {
      date_cast: '',
      truck_no: '',
      no_cast: '',
      pile_number: '',
      location: '',
      mix: '',
      slump_mm: '',
      temperature_c: '',
      de_moulded: '',
      specimen: '',
      break_7_day: '',
      break_14_day: '',
      break_28_day: ''
    };
  }

  let loading = $state(fieldTestId !== null);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The id once saved; a new log gets one on its first save. */
  let savedId = $state<number | null>(fieldTestId);

  let siteId = $state<number | null>(null);
  let title = $state('');
  let notes = $state('');

  let rows = $state<RowState[]>(fieldTestId === null ? [blankRow()] : []);

  // Opened from a site page: pre-select that site and fill the title from
  // it (only when the title is still blank). Only ever on a NEW form, so
  // this can never overwrite something the user has typed or loaded.
  $effect(() => {
    const fromSite = siteIdFromQuery($appLocation.query);
    if (fieldTestId === null && fromSite !== null && siteId === null) {
      siteId = fromSite;
      api
        .getSite(fromSite)
        .then((response) => {
          if (!title.trim()) title = response.site.name;
        })
        .catch(() => {
          // The typed title text still saves even if the name lookup fails.
        });
    }
  });

  function chooseSite(site: SiteSummary | null) {
    siteId = site?.id ?? null;
    if (site && !title.trim()) title = site.name;
  }

  $effect(() => {
    if (fieldTestId === null) return;
    loading = true;
    api
      .getFieldTest(fieldTestId)
      .then((response) => {
        const log = response.field_test_log;
        siteId = log.site_id;
        title = log.title;
        notes = log.notes;
        rows = response.rows.map((row) => ({
          date_cast: row.date_cast,
          truck_no: row.truck_no,
          no_cast: row.no_cast,
          pile_number: row.pile_number,
          location: row.location,
          mix: row.mix,
          slump_mm: row.slump_mm,
          temperature_c: row.temperature_c,
          de_moulded: row.de_moulded,
          specimen: row.specimen,
          break_7_day: row.break_7_day,
          break_14_day: row.break_14_day,
          break_28_day: row.break_28_day
        }));
        savedId = log.id;
        // The loaded document is the auto-save baseline: nothing is dirty yet.
        lastSaved = JSON.stringify(body());
      })
      .catch((cause) => {
        notifyError(cause, 'Could not load that field test log');
        error = 'Could not load that field test log.';
      })
      .finally(() => {
        loading = false;
      });
  });

  function addRow() {
    rows = [...rows, blankRow()];
  }

  /**
   * The walk order for the template: highest array index first, so the newest
   * row displays at the top and the oldest settles to the bottom. Purely a
   * view-order — save, print and seq all keep the array's own order.
   */
  const displayOrder = $derived(rows.map((_, i) => i).reverse());

  /**
   * The row awaiting a "really remove?" confirmation, or null.
   *
   * Only rows that actually hold something ask. A blank row is scaffolding —
   * making someone confirm removing nothing trains them to dismiss the
   * confirmation without reading it, which is exactly how the one that matters
   * gets waved through.
   */
  let confirmingRemove = $state<number | null>(null);

  function rowHasContent(row: RowState): boolean {
    return Object.values(row).some((value) => (value ?? '').trim() !== '');
  }

  function removeRow(index: number) {
    if (rowHasContent(rows[index])) {
      confirmingRemove = index;
      return;
    }
    rows = rows.filter((_, i) => i !== index);
  }

  function confirmRemoveRow(index: number) {
    rows = rows.filter((_, i) => i !== index);
    confirmingRemove = null;
  }

  function moveRow(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    rows = next;
  }

  function rowsForSave(): FieldTestRowInput[] {
    return rows.map((row) => ({ ...row }));
  }

  function body(): SaveFieldTestLogRequest {
    return { site_id: siteId, title, notes, rows: rowsForSave() };
  }

  let printing = $state(false);

  /**
   * Print what is on screen, not what was last saved: the author expects the
   * document to match the form in front of them. Ids and timestamps are
   * placeholders because the builder does not print them.
   */
  function print() {
    if (printing) return;
    printing = true;
    const stamp = new Date().toISOString();
    const started = printFieldTest(
      {
        field_test_log: {
          id: savedId ?? 0,
          title,
          notes,
          site_id: siteId,
          created_at: stamp,
          updated_at: stamp
        },
        rows: rows.map((row, index) => ({
          id: index + 1,
          log_id: savedId ?? 0,
          seq: index + 1,
          ...row
        })),
        exported_at: stamp
      },
      {
        onDone: (cause) => {
          printing = false;
          if (cause) {
            notify({
              tone: 'warn',
              title: 'Could not open the print view',
              detail: 'Try again, or use your browser’s own Print command.',
              sticky: true
            });
          }
        }
      }
    );

    if (!started) {
      printing = false;
      notify({
        tone: 'warn',
        title: 'This browser will not print',
        detail: 'Use your browser’s own Print command instead.',
        sticky: true
      });
    }
  }

  async function save(opts: { auto?: boolean } = {}) {
    if (saving || removed) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      const response =
        savedId === null
          ? await api.createFieldTest(body())
          : await api.updateFieldTest(savedId, body());

      const isNew = savedId === null;
      savedId = response.field_test_log.id;
      // The baseline is what the form NOW holds, so nothing reads as a fresh edit.
      lastSaved = JSON.stringify(body());
      if (!opts.auto) {
        notify({ tone: 'ok', title: isNew ? 'Field test log created' : 'Field test log saved' });
      }
      // A new log gets its own address, so a refresh or a back button lands
      // on the saved sheet rather than a blank one. An AUTO save only
      // rewrites the address bar: `navigate` would change the route param and
      // the {#key} around this screen would tear the form down mid-keystroke.
      if (isNew) {
        if (opts.auto) claimAddress(routePath.qualityFieldTest(savedId));
        else navigate(routePath.qualityFieldTest(savedId), { replace: true, keepScroll: true });
      }
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /* --- auto-save (lib/autosave.ts; same wiring as DailyReportForm) -------- */

  /** JSON of the document at its last save/load — the dirty check's anchor. */
  let lastSaved = $state<string | null>(null);
  /** Set once the log is deleted, so no timer can quietly resurrect it. */
  let removed = false;

  /**
   * Does a NEW form hold something worth creating a record for? The site
   * prefill (siteId + title) and the one seeded blank row deliberately do
   * not count — only something a person typed.
   */
  function hasContent(): boolean {
    return notes.trim() !== '' || rows.some(rowHasContent);
  }

  const dirty = $derived(lastSaved !== null && JSON.stringify(body()) !== lastSaved);

  const autosave = createAutosave({
    snapshot: () => JSON.stringify(body()),
    saved: () => lastSaved,
    busy: () => saving || deleting,
    blocked: () => removed,
    ready: () => savedId !== null || hasContent(),
    save: () => void save({ auto: true })
  });

  $effect(() => {
    const snapshot = JSON.stringify(body()); // subscribes to every field
    if (loading) return;
    if (snapshot === lastSaved) return;
    autosave.changed();
  });

  // A timer must not outlive the form (navigation away mid-edit).
  $effect(() => {
    return () => autosave.stop();
  });

  /**
   * Delete, behind an in-page confirmation step rather than a confirm() —
   * the app uses no modal dialogs anywhere.
   */
  let confirmingDelete = $state(false);

  async function remove() {
    if (savedId === null || deleting) return;
    deleting = true;
    try {
      await api.deleteFieldTest(savedId);
      // No auto-save timer may quietly resurrect what was just deleted.
      removed = true;
      autosave.stop();
      notify({ tone: 'ok', title: 'Field test log deleted' });
      navigate(routePath.qualityFieldTestList(siteId ?? undefined), { replace: true });
    } catch (cause) {
      notifyError(cause, 'Could not delete that field test log');
    } finally {
      deleting = false;
      confirmingDelete = false;
    }
  }
</script>

<!-- Enter moves to the next field; see lib/fields/advance-on-enter.ts. -->
<div use:advanceOnEnter>
{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else}
  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Could not save</strong>{error}</div>
    </div>
  {/if}

  <div class="card">
    <div class="card__body">
      <div class="titlebar">
        <h1 class="card__title">{savedId === null ? 'New field test log' : title || 'Field test log'}</h1>
        <a class="btn btn--sm" href={routePath.qualityFieldTestList(siteId ?? undefined)} onclick={linkHandler}>
          All field test logs
        </a>
      </div>

      <SitePicker value={siteId} onchoose={chooseSite} />

      <label class="field">
        <span class="label">Title</span>
        <input type="text" bind:value={title} placeholder="Concrete Field Testing" />
        {#if fieldErrors.title}<span class="field__flag">{fieldErrors.title}</span>{/if}
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <h2 class="card__title">Cylinder sets</h2>

      <!-- Above the list, not below it: new rows appear at the top, so the
           control that makes one belongs there too. -->
      <button class="btn btn--block addrow" type="button" onclick={addRow}>Add row</button>

      <div class="ftrows">
        {#each displayOrder as index (index)}
          <div class="ftrow">
            <!--
              The row's identity and its controls live at the TOP of the block,
              together.

              They used to sit at the bottom, ~570px below the fields that say
              which row this is. Stacked on a phone that is taller than the
              screen, so at any scroll position showing a row's fields, the
              nearest visible ✕ belonged to the row ABOVE — people deleted the
              wrong row and reported losing earlier ones. The list logic was
              always correct; the control was just nowhere near what it acted on.
            -->
            <div class="ftrow__head" class:ftrow__head--confirm={confirmingRemove === index}>
              {#if confirmingRemove === index}
                <!--
                  Asked in place, in this row's own header, rather than as a
                  dialog: the row it is about stays on screen underneath, so
                  you can see what you are about to lose before answering.
                -->
                <span class="ftrow__name">Remove row {index + 1}?</span>
                <div class="ftrow__actions">
                  <button
                    class="btn btn--sm btn--danger"
                    type="button"
                    onclick={() => confirmRemoveRow(index)}
                  >Remove</button>
                  <button
                    class="btn btn--sm"
                    type="button"
                    onclick={() => (confirmingRemove = null)}
                  >Keep</button>
                </div>
              {:else}
                <span class="ftrow__name">
                  Row {index + 1}{rows[index].pile_number ? ` · ${rows[index].pile_number}` : ''}
                </span>
                <div class="ftrow__actions">
                  <!-- The list displays newest-first, so VISUAL up is array
                       index + 1 and visual down is index − 1. The labels
                       describe what the reader sees. -->
                  <button
                    class="btn btn--sm btn--ghost"
                    type="button"
                    onclick={() => moveRow(index, 1)}
                    disabled={index === rows.length - 1}
                    aria-label="Move row {index + 1} up"
                  >↑</button>
                  <button
                    class="btn btn--sm btn--ghost"
                    type="button"
                    onclick={() => moveRow(index, -1)}
                    disabled={index === 0}
                    aria-label="Move row {index + 1} down"
                  >↓</button>
                  <button
                    class="btn btn--sm btn--ghost"
                    type="button"
                    onclick={() => removeRow(index)}
                    aria-label="Remove row {index + 1}"
                  >✕</button>
                </div>
              {/if}
            </div>

            <div class="ftrow__fields">
              <label class="ftrow__field">
                <span class="label">Date cast</span>
                <input type="date" bind:value={rows[index].date_cast} aria-label="Row {index + 1} date cast" />
              </label>
              <label class="ftrow__field">
                <span class="label">Truck no.</span>
                <input type="text" bind:value={rows[index].truck_no} aria-label="Row {index + 1} truck number" />
              </label>
              <label class="ftrow__field">
                <span class="label">No. cast</span>
                <input type="text" bind:value={rows[index].no_cast} aria-label="Row {index + 1} number cast" />
              </label>
              <label class="ftrow__field">
                <span class="label">Pile number</span>
                <input type="text" bind:value={rows[index].pile_number} aria-label="Row {index + 1} pile number" />
              </label>
              <label class="ftrow__field">
                <span class="label">Location</span>
                <input type="text" bind:value={rows[index].location} aria-label="Row {index + 1} location" />
              </label>
              <label class="ftrow__field">
                <span class="label">Mix</span>
                <input type="text" bind:value={rows[index].mix} aria-label="Row {index + 1} mix" />
              </label>
              <label class="ftrow__field">
                <span class="label">Slump</span>
                <!-- Text, not number: '150*' and similar are real printed values. -->
                <input type="text" bind:value={rows[index].slump_mm} aria-label="Row {index + 1} slump" />
              </label>
              <label class="ftrow__field">
                <span class="label">Temperature</span>
                <input type="text" bind:value={rows[index].temperature_c} aria-label="Row {index + 1} temperature" />
              </label>
              <label class="ftrow__field">
                <span class="label">De-moulded</span>
                <input type="text" bind:value={rows[index].de_moulded} aria-label="Row {index + 1} de-moulded" />
              </label>
              <label class="ftrow__field">
                <span class="label">Specimen</span>
                <input type="text" bind:value={rows[index].specimen} aria-label="Row {index + 1} specimen" />
              </label>
              <label class="ftrow__field">
                <span class="label">7 day</span>
                <!-- Text, not number: 'Not recorded' is a real printed value. -->
                <input type="text" bind:value={rows[index].break_7_day} aria-label="Row {index + 1} 7 day break" />
              </label>
              <label class="ftrow__field">
                <span class="label">14 day</span>
                <input type="text" bind:value={rows[index].break_14_day} aria-label="Row {index + 1} 14 day break" />
              </label>
              <label class="ftrow__field">
                <span class="label">28 day</span>
                <input type="text" bind:value={rows[index].break_28_day} aria-label="Row {index + 1} 28 day break" />
              </label>
            </div>

          </div>
        {/each}

        {#if rows.length === 0}
          <p class="meta">No rows yet.</p>
        {/if}
      </div>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <label class="field">
        <span class="label">Notes</span>
        <textarea rows="5" bind:value={notes}></textarea>
      </label>
    </div>
  </div>

  <div class="card">
    <div class="card__body">
      <button class="btn btn--block" type="button" disabled={printing} onclick={print}>
        {#if printing}<span class="spinner" aria-hidden="true"></span> Preparing…{:else}Print / Save as PDF{/if}
      </button>

      <button class="btn btn--primary btn--block btn--big" type="button" disabled={saving} onclick={() => save()}>
        {#if saving}<span class="spinner" aria-hidden="true"></span> Saving…{:else}Save field test log{/if}
      </button>

      <!-- The auto-save's word on where things stand. Only ever informational:
           the button above still saves on demand. -->
      <p class="autosave" role="status">
        {#if saving}
          Saving…
        {:else if savedId !== null && !dirty}
          All changes saved.
        {:else if savedId !== null || hasContent()}
          Unsaved changes — saving automatically…
        {:else}
          Saves automatically as you fill it in.
        {/if}
      </p>

      {#if $isAdmin && savedId !== null}
        {#if confirmingDelete}
          <div class="banner banner--warn" role="status">
            <div class="spacer">
              <strong>Delete this field test log?</strong>
              This cannot be undone.
              <div class="banner__actions">
                <button class="btn btn--sm btn--danger" type="button" disabled={deleting} onclick={remove}>
                  {#if deleting}Deleting…{:else}Delete{/if}
                </button>
                <button class="btn btn--sm" type="button" onclick={() => (confirmingDelete = false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        {:else}
          <button class="btn btn--block" type="button" onclick={() => (confirmingDelete = true)}>
            Delete field test log
          </button>
        {/if}
      {/if}
    </div>
  </div>
{/if}
</div>

<style>
  /* Informational, never alarming: where the auto-save stands. */
  .autosave {
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    color: var(--ink-faint);
    text-align: center;
  }

  .titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
  }

  .titlebar .card__title {
    margin: 0;
  }

  /*
    Thirteen data columns, one action column: even at floors as tight as
    4.5rem per data column (which would already be too cramped to hold a
    date input and a 3-4 character break result comfortably), 13 columns
    plus 12 gaps plus a 3-button action column comes to roughly 1267px —
    over the 64rem/1088px office container's own ceiling, before even
    reaching the narrower ~875-890px actually available at the 60rem/960px
    breakpoint the other tables in this feature switch at. There is no floor
    narrow enough to make a one-line row here fit without side-scrolling, so
    unlike .rows--checklist (EquipmentForm) or .rows--activity
    (DailyReportForm) there is no --rowcols / 60rem media query pair at all:
    this table is always the stacked, per-field-labelled block those other
    tables only use below their breakpoint.

    Within that block, auto-fit + minmax(min(7.5rem, 100%), 1fr) is the same
    technique MixDesignForm's .material__fields uses below 60rem: the min()
    caps the floor at the container's own width, so a track can always
    shrink to fit rather than force a side-scroll, and auto-fit packs as
    many fields per line as the current width allows — several per line on
    a laptop, one per line on a phone — without ever needing a breakpoint of
    its own.
  */
  .ftrows {
    display: flex;
    flex-direction: column;
  }

  /* Breathing room under the Add-row control that now leads the list. */
  .addrow {
    margin-bottom: 0.7rem;
  }

  /* A row is a card, not a stretch of a longer list. Stacked on a phone one row
     is taller than the screen, so without a visible edge there is nothing to
     tell you where one cylinder set ends and the next begins. */
  .ftrow {
    border: 1px solid var(--line-soft);
    border-radius: var(--radius);
    padding: 0.6rem 0.7rem 0.85rem;
    margin-bottom: 0.7rem;
  }

  .ftrow:last-of-type {
    margin-bottom: 0;
  }

  /* Sticks to the top of the viewport while you scroll through this row's
     fields, so the ✕ you can see always belongs to the row you are looking at.
     That adjacency is the whole point — see the comment in the markup. */
  /* NOT sticky, deliberately. Pinning this header while you scroll a tall
     row would be nicer still, but an ancestor .card sets overflow:hidden,
     which disables position:sticky for everything inside it — and .card is
     shared with the field QC screens that are in production, so widening its
     overflow to buy a nicety here is not a trade worth making. The header
     naming its own row is what removes the ambiguity; being pinned would
     only have made it prettier. */
  .ftrow__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: -0.6rem -0.7rem 0.6rem;
    padding: 0.4rem 0.7rem;
    background: var(--bg-sunken);
    border-radius: var(--radius) var(--radius) 0 0;
    border-bottom: 1px solid var(--line-soft);
  }

  /* Warned, not alarmed: enough colour to say "this one is asking something"
     without turning a routine tidy-up into a scare. */
  .ftrow__head--confirm {
    background: var(--warn-tint);
    border-bottom-color: var(--warn);
  }

  .ftrow__name {
    font-weight: 700;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ftrow__fields {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(7.5rem, 100%), 1fr));
    gap: 0.6rem;
  }

  .ftrow__field {
    display: block;
    min-width: 0;
  }

  .ftrow__field .label {
    margin-bottom: 0.25rem;
  }

  .ftrow__actions {
    display: flex;
    gap: 0.3rem;
    flex: none;
  }
</style>
